import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { CommandPalette } from './CommandPalette';
import { NotificationsDrawer } from './NotificationsDrawer';
import { ShortcutsHelp } from './ShortcutsHelp';
import { useAuth } from '@/store/auth';
import { useT } from '@/store/i18n';
import { toast } from '@/components/ui/Toast';
import { api } from '@/lib/api';

export function AppShell() {
  const navigate = useNavigate();
  const role = useAuth((s) => s.user?.role);
  const t = useT();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const lastKeyRef = useRef<{ key: string; ts: number } | null>(null);
  const prevUnreadRef = useRef<number>(0);

  // Global keyboard shortcuts
  useEffect(() => {
    const inField = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
    };
    const onKey = (e: KeyboardEvent) => {
      // Always handle: Cmd+K, ?, /
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      if (inField(e.target)) return;
      if (e.key === '?' && e.shiftKey) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (e.key === '/') {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (e.key === 'n' || e.key === 'N') {
        if (role && (role === 'teacher' || role === 'academic_admin' || role === 'admin')) {
          e.preventDefault();
          navigate('/questions/new');
          return;
        }
      }
      // 2-key sequences: G then [D|Q|B|A|R|U|M]
      const now = Date.now();
      const prev = lastKeyRef.current;
      const expired = !prev || now - prev.ts > 1000;
      if (e.key === 'g' || e.key === 'G') {
        lastKeyRef.current = { key: 'g', ts: now };
        return;
      }
      if (!expired && prev?.key === 'g') {
        const map: Record<string, string> = {
          d: '/', q: '/questions', b: '/question-bank', a: '/approvals',
          r: '/reports', u: '/users', m: '/metadata',
        };
        const target = map[e.key.toLowerCase()];
        if (target) {
          e.preventDefault();
          navigate(target);
          lastKeyRef.current = null;
          return;
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, role]);

  // Poll unread notification count every 30s. Toast when count goes up.
  const unread = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: async () => {
      try {
        const r = await api<{ data?: { unread_count?: number; count?: number } }>('notifications/index.php?unread_only=true&limit=1');
        return r.data?.unread_count ?? r.data?.count ?? 0;
      } catch {
        return 0;
      }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
    retry: false,
  });

  // Toast when unread count increases (new notification arrived between polls)
  useEffect(() => {
    const current = unread.data ?? 0;
    const prev = prevUnreadRef.current;
    if (current > prev && prev !== 0) {
      const delta = current - prev;
      toast.info(t('notif.new_arrived'), `${delta} ${t('notif.new_count')}`);
    }
    prevUnreadRef.current = current;
  }, [unread.data, t]);

  return (
    <div className="min-h-screen bg-surface-muted">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <NotificationsDrawer open={notifOpen} onClose={() => setNotifOpen(false)} />
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />

      <div className="lg:pl-64">
        <Topbar
          onMenuClick={() => setSidebarOpen(true)}
          onSearchClick={() => setPaletteOpen(true)}
          onNotificationsClick={() => setNotifOpen(true)}
          unreadCount={unread.data ?? 0}
        />
        <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
